import clsx from 'clsx';
import { forwardRef, type PropsWithChildren } from 'react';

import {
  type StylingProps,
  type MaskingProps,
  type DataTestId,
} from '@dynatrace/strato-components/core';
import { Flex, Grid } from '@dynatrace/strato-components/layouts';

import { METER_BAR_SIZE } from '../../constants.js';
import { useComputedSegmentSize } from './hooks/useComputedSegmentSize.js';
import {
  chartContainer,
  labelContainer,
  legendContainer,
  maxContainer,
  minContainer,
  valueContainer,
  container,
} from './MeterBarLayout.sty.js';
import { Chart } from './slots/Chart.js';
import { MeterBarProviders } from '../meter-bar-providers/MeterBarProviders.js';
import { Icon } from './slots/Icon/Icon.js';
import { Label } from './slots/Label/Label.js';
import { Legend } from './slots/Legend.js';
import { Max } from './slots/Max/Max.js';
import { Min } from './slots/Min/Min.js';
import { Value } from './slots/Value/Value.js';
import { extractSlots } from './utils/extract-slots.js';
import type { AriaLabel } from '../../../../core/types/aria-label.js';
import type { ComponentWithName } from '../../../../core/types/component-with-name.js';
import { MeterBarSize } from '../../types/meter-bars.js';

export interface MeterBarLayoutProps
  extends StylingProps,
    MaskingProps,
    DataTestId,
    AriaLabel {
  /**
   * The size that applies to the value, icon, and label.
   * @defaultvalue 'size16'
   */
  size: MeterBarSize;
}

/**
 * Component responsible for defining the layout of the meter bars.
 * The layout is divided in the labels area (icon, label and value), chart area and legend area.
 */
export const MeterBarLayoutRoot = forwardRef<
  HTMLDivElement,
  PropsWithChildren<MeterBarLayoutProps>
>(
  (
    {
      size,
      children,
      className,
      style,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-testid': dataTestId,
      'aria-label': ariaLabel,
    },
    forwardedRef,
  ) => {
    const { label, min, max, value, chart, icon, legend } =
      extractSlots(children);

    const hasAutoSize = size === METER_BAR_SIZE.Auto;
    const gridTemplateRows = hasAutoSize ? 'auto 1fr auto auto' : 'auto';

    const {
      segmentSize,
      valueLabelRef,
      labelRef,
      minLabelRef,
      maxLabelRef,
      plotAreaRef,
      legendRef,
    } = useComputedSegmentSize();

    return (
      <Grid
        className={clsx(container({ autoSize: hasAutoSize }), className)}
        style={style}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        ref={forwardedRef}
        gridTemplateAreas="
            'label label label value'
            'chart chart chart chart'
            'min min max max'
            'legend legend legend legend'"
        gridTemplateColumns="25% 25% 25% 25%"
        gridTemplateRows={gridTemplateRows}
        rowGap={0}
        columnGap={0}
        aria-label={ariaLabel}
        role={'img'}
      >
        <MeterBarProviders
          size={size}
          segmentHeight={segmentSize}
          data-dtrum-allow={dataDtrumAllow}
          data-dtrum-mask={dataDtrumMask}
        >
          {(label || icon) && (
            <Flex
              ref={labelRef}
              alignItems="center"
              alignSelf="center"
              className={labelContainer}
            >
              {icon && icon}
              {label && label}
            </Flex>
          )}
          {value && (
            <div ref={valueLabelRef} className={valueContainer}>
              {value}
            </div>
          )}
          <div className={chartContainer} ref={plotAreaRef}>
            {chart}
          </div>
          {min && (
            <div ref={minLabelRef} className={minContainer}>
              {min}
            </div>
          )}
          {max && (
            <div ref={maxLabelRef} className={maxContainer}>
              {max}
            </div>
          )}
          {legend && (
            <div ref={legendRef} className={legendContainer}>
              {legend}
            </div>
          )}
        </MeterBarProviders>
      </Grid>
    );
  },
);

(
  MeterBarLayoutRoot as ComponentWithName<typeof MeterBarLayoutRoot>
).displayName = 'MeterBarLayoutRoot';

/** Slot component for meter bar */
export const MeterBarLayout = Object.assign(MeterBarLayoutRoot, {
  Label,
  Min,
  Max,
  Chart,
  Value,
  Icon,
  Legend,
});
